import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import CalculatorBridge from './CalculatorBridge';
import { FinancialConfig, normalizeGeneralTransferBalanceCap } from './FinancialConfig';
import SubscriptionManager from './SubscriptionManager';
import { trackTransferReceived } from './utils/funnelAnalytics.js';
import { normalizeLumpSumWithdrawalRows } from './lumpSumWithdrawals.js';
import { normalizeLumpSumContributionRows } from './lumpSumContributions.js';

const AdvancedStateContext = createContext(null);

// Load saved state from LocalStorage
const loadSavedState = () => {
  // Check for transfer data first (from other calculators or SMSF)
  const transferData = CalculatorBridge.retrieveTransferData('advanced');
  if (transferData) {
    console.log('✅ loadSavedState: Found transfer data');
    console.log('  - sp500:', transferData.sp500);
    console.log('  - ausShares:', transferData.ausShares);
    console.log('  - cash:', transferData.cash);
    // Clear old localStorage to prevent it from overwriting transfer data
    localStorage.removeItem('advanced_calculator_state');
    console.log('✅ loadSavedState: Returning transfer data');
    // DO NOT clear transfer data here - the useEffect needs it too
    return transferData;
  }

  // Otherwise load saved state
  console.log('loadSavedState: No transfer data, checking localStorage');
  try {
    const saved = localStorage.getItem('advanced_calculator_state');
    if (saved) {
      console.log('loadSavedState: Found saved state in localStorage');
      return JSON.parse(saved);
    }
  } catch (err) {
    console.error('Failed to load saved state:', err);
  }
  console.log('loadSavedState: No saved state, returning empty object');
  return {};
};

export const AdvancedStateProvider = ({ children }) => {
  const savedState = loadSavedState();

  // Free visitors get runnable defaults (age/super/period + 100% allocation).
  // Subscribers / Basic→Advanced transfers keep saved or transferred values.
  const isSubscriber = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO);
  const isBasicTransferBootstrap =
    savedState?.startYear !== undefined &&
    savedState?.sp500 !== undefined &&
    (savedState.yourSuper !== undefined && savedState.yourSuper !== '');
  const FREE_PERSONAL_DEFAULTS = {
    yourAge: 65,
    periodLength: 30,
    yourSuper: 500000,
    partnerSuper: '',
    partnerAge: 65,
  };
  const personal = (key) => {
    if (savedState[key] !== undefined && savedState[key] !== '') return savedState[key];
    if (isSubscriber || isBasicTransferBootstrap) return savedState[key] ?? '';
    return FREE_PERSONAL_DEFAULTS[key] ?? '';
  };
  // Saved all-zeros allocation is treated as unset (not a deliberate empty portfolio).
  const BALANCED_ALLOC = { sp500: 35, ausShares: 15, intlShares: 20, bonds: 20, cash: 10 };
  const rawAlloc = {
    sp500: Number(savedState.sp500) || 0,
    ausShares: Number(savedState.ausShares) || 0,
    intlShares: Number(savedState.intlShares) || 0,
    bonds: Number(savedState.bonds) || 0,
    cash: Number(savedState.cash) || 0,
  };
  const rawAllocSum =
    rawAlloc.sp500 + rawAlloc.ausShares + rawAlloc.intlShares + rawAlloc.bonds + rawAlloc.cash;
  const resolvedAlloc = Math.abs(rawAllocSum) < 0.5 ? BALANCED_ALLOC : rawAlloc;
  
  const [relationshipStatus, setRelationshipStatus] = useState(savedState.relationshipStatus || 'single');
  const [yourGender, setYourGender] = useState(savedState.yourGender || 'male');
  const [yourAge, setYourAge] = useState(personal('yourAge'));
  const [startYear, setStartYear] = useState(savedState.startYear || 1980);
  const [periodLength, setPeriodLength] = useState(personal('periodLength'));
  const [targetFinalBalance, setTargetFinalBalance] = useState(savedState.targetFinalBalance ?? 0);
  const [yourSuper, setYourSuper] = useState(personal('yourSuper'));
  const [yourTbcUsed, setYourTbcUsed] = useState(savedState.yourTbcUsed || 0);
  const [partnerGender, setPartnerGender] = useState(savedState.partnerGender || 'female');
  const [partnerSuper, setPartnerSuper] = useState(personal('partnerSuper'));
  const [partnerAge, setPartnerAge] = useState(personal('partnerAge') || 65);
  const [partnerTbcUsed, setPartnerTbcUsed] = useState(savedState.partnerTbcUsed || '');
  const [otherTaxableIncome, setOtherTaxableIncome] = useState(savedState.otherTaxableIncome || '');
  const [yourPartTimeEmploymentIncome, setYourPartTimeEmploymentIncome] = useState(savedState.yourPartTimeEmploymentIncome || '');
  const [partnerPartTimeEmploymentIncome, setPartnerPartTimeEmploymentIncome] = useState(savedState.partnerPartTimeEmploymentIncome || '');
  const [isHomeowner, setIsHomeowner] = useState(savedState.isHomeowner !== undefined ? savedState.isHomeowner : true);
  const [otherNonTaxableIncome, setOtherNonTaxableIncome] = useState(savedState.otherNonTaxableIncome || '');
  const [otherAssets, setOtherAssets] = useState(savedState.otherAssets || '');
  const [includePension, setIncludePension] = useState(savedState.includePension !== undefined ? savedState.includePension : true);
  const [pensionGrowthRate, setPensionGrowthRate] = useState(savedState.pensionGrowthRate || 0.3);
  const [generalTransferBalanceCap, setGeneralTransferBalanceCap] = useState(() =>
    normalizeGeneralTransferBalanceCap(
      savedState.generalTransferBalanceCap ??
        savedState.transferBalanceCap ??
        FinancialConfig.TRANSFER_BALANCE_CAP
    )
  );
  const [hasPrivateHealthInsurance, setHasPrivateHealthInsurance] = useState(savedState.hasPrivateHealthInsurance !== undefined ? savedState.hasPrivateHealthInsurance : true);
  const [partnerHasPrivateHealthInsurance, setPartnerHasPrivateHealthInsurance] = useState(savedState.partnerHasPrivateHealthInsurance !== undefined ? savedState.partnerHasPrivateHealthInsurance : true);
  const [withdrawalStrategy, setWithdrawalStrategy] = useState(savedState.withdrawalStrategy || 'fixed');
  
  // Phased Retirement State
  const [phasedRetirement, setPhasedRetirement] = useState(savedState.phasedRetirement || false);
  const [yourRetirementAge, setYourRetirementAge] = useState(savedState.yourRetirementAge || savedState.yourAge || 67);
  const [partnerRetirementAge, setPartnerRetirementAge] = useState(savedState.partnerRetirementAge || savedState.partnerAge || 67);
  const [partnerSalary, setPartnerSalary] = useState(savedState.partnerSalary || '');
  const [partnerSuperContributions, setPartnerSuperContributions] = useState(savedState.partnerSuperContributions || '');
  const [yourSalary, setYourSalary] = useState(savedState.yourSalary || '');
  const [yourSuperContributions, setYourSuperContributions] = useState(savedState.yourSuperContributions || '');
  // When phased: start Fixed MSI lifestyle drawdowns when either partner retires (default), or only when you retire
  const [msiStartsWhenEitherRetires, setMsiStartsWhenEitherRetires] = useState(() => {
    const v = savedState.msiStartsWhenEitherRetires;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
    return true;
  });
  const [monteCarloTargetIncome, setMonteCarloTargetIncome] = useState(
    savedState.monteCarloTargetIncome ?? ''
  );
  
  // Super withdrawal age control
  const [allowWithdrawalsBeforeRetirementAge, setAllowWithdrawalsBeforeRetirementAge] = useState(
    savedState.allowWithdrawalsBeforeRetirementAge !== undefined ? savedState.allowWithdrawalsBeforeRetirementAge : false
  );
  const [enableBridgeStrategy, setEnableBridgeStrategy] = useState(savedState.enableBridgeStrategy || false);
  const [yourSuperAccessAge, setYourSuperAccessAge] = useState(savedState.yourSuperAccessAge || savedState.yourRetirementAge || savedState.yourAge || 67);
  const [partnerSuperAccessAge, setPartnerSuperAccessAge] = useState(savedState.partnerSuperAccessAge || savedState.partnerRetirementAge || savedState.partnerAge || 67);
  const [bridgeCashBucket, setBridgeCashBucket] = useState(savedState.bridgeCashBucket || '');
  const [bridgeInvestmentBucket, setBridgeInvestmentBucket] = useState(savedState.bridgeInvestmentBucket || '');
  const [bridgeInvestmentReturnRate, setBridgeInvestmentReturnRate] = useState(savedState.bridgeInvestmentReturnRate || 3);
  const [enableMortgageComparison, setEnableMortgageComparison] = useState(savedState.enableMortgageComparison || false);
  const [mortgageBalance, setMortgageBalance] = useState(savedState.mortgageBalance || '');
  const [mortgageInterestRate, setMortgageInterestRate] = useState(savedState.mortgageInterestRate || 6);
  const [mortgageAnnualRepayment, setMortgageAnnualRepayment] = useState(savedState.mortgageAnnualRepayment || '');
  const [mortgageStrategy, setMortgageStrategy] = useState(savedState.mortgageStrategy || 'keep');
  const [mortgagePayoffSource, setMortgagePayoffSource] = useState(savedState.mortgagePayoffSource || 'super');
  const [mortgagePayoffScheduleType, setMortgagePayoffScheduleType] = useState(savedState.mortgagePayoffScheduleType || 'year');
  const [mortgagePayoffScheduleValue, setMortgagePayoffScheduleValue] = useState(savedState.mortgagePayoffScheduleValue || '');
  const [mortgagePartialPayoffAmount, setMortgagePartialPayoffAmount] = useState(savedState.mortgagePartialPayoffAmount || '');
  
  // Basic calculator assumed return / inflation (Adviser Basic View tab)
  const [assumedReturn, setAssumedReturn] = useState(
    savedState.assumedReturn != null && savedState.assumedReturn !== '' ? Number(savedState.assumedReturn) : 6.5
  );
  const [assumedInflation, setAssumedInflation] = useState(
    savedState.assumedInflation != null && savedState.assumedInflation !== '' ? Number(savedState.assumedInflation) : 2.5
  );

  const [adminFees, setAdminFees] = useState(savedState.adminFees ?? 52);
  const [adminFeePercentage, setAdminFeePercentage] = useState(savedState.adminFeePercentage ?? 0.10);
  const [investmentFees, setInvestmentFees] = useState(savedState.investmentFees ?? 0.5);
  const [investmentFeeCap, setInvestmentFeeCap] = useState(savedState.investmentFeeCap || 350);
  const [transactionCosts, setTransactionCosts] = useState(savedState.transactionCosts || 0.08);
  const [insuranceFees, setInsuranceFees] = useState(savedState.insuranceFees || '');
  // Balanced 100% when unset / all-zero saved weights.
  const [sp500, setSp500] = useState(resolvedAlloc.sp500);
  const [ausShares, setAusShares] = useState(resolvedAlloc.ausShares);
  const [intlShares, setIntlShares] = useState(resolvedAlloc.intlShares);
  const [bonds, setBonds] = useState(resolvedAlloc.bonds);
  const [cash, setCash] = useState(resolvedAlloc.cash);
  const [frankingCredits, setFrankingCredits] = useState(savedState.frankingCredits || 80);
  // Last successful run headline — survives Stripe cancel / reload for paywall copy.
  const [lastResultIncome, setLastResultIncome] = useState(
    Number(savedState.lastResultIncome) > 0 ? Number(savedState.lastResultIncome) : null
  );
  const [lastResultSurvival, setLastResultSurvival] = useState(
    savedState.lastResultSurvival != null && savedState.lastResultSurvival !== ''
      ? savedState.lastResultSurvival
      : null
  );
  const [annuityPurchasePrice, setAnnuityPurchasePrice] = useState(savedState.annuityPurchasePrice || '');
  const [annuityAnnualPayment, setAnnuityAnnualPayment] = useState(savedState.annuityAnnualPayment || '');
  const [annuityIsIndexed, setAnnuityIsIndexed] = useState(savedState.annuityIsIndexed !== undefined ? savedState.annuityIsIndexed : true);
  const [definedBenefitPensionAmount, setDefinedBenefitPensionAmount] = useState(savedState.definedBenefitPensionAmount || '');
  const [definedBenefitIsIndexed, setDefinedBenefitIsIndexed] = useState(savedState.definedBenefitIsIndexed !== undefined ? savedState.definedBenefitIsIndexed : true);
  
  // Alternative Assets state variables (from SMSF transfers)
  const [transferFromSMSF, setTransferFromSMSF] = useState(savedState.transferFromSMSF || false);
  const [residentialPropertyValue, setResidentialPropertyValue] = useState(savedState.residentialPropertyValue || '');
  const [residentialRentalIncome, setResidentialRentalIncome] = useState(savedState.residentialRentalIncome || '');
  const [residentialPropertyRentalYield, setResidentialPropertyRentalYield] = useState(savedState.residentialPropertyRentalYield || '');
  const [residentialPropertyCapitalGrowth, setResidentialPropertyCapitalGrowth] = useState(savedState.residentialPropertyCapitalGrowth || '');
  const [commercialPropertyValue, setCommercialPropertyValue] = useState(savedState.commercialPropertyValue || '');
  const [commercialRentalIncome, setCommercialRentalIncome] = useState(savedState.commercialRentalIncome || '');
  const [commercialPropertyRentalYield, setCommercialPropertyRentalYield] = useState(savedState.commercialPropertyRentalYield || '');
  const [commercialPropertyCapitalGrowth, setCommercialPropertyCapitalGrowth] = useState(savedState.commercialPropertyCapitalGrowth || '');
  const [collectiblesValue, setCollectiblesValue] = useState(savedState.collectiblesValue || '');
  const [collectiblesCapitalGrowth, setCollectiblesCapitalGrowth] = useState(savedState.collectiblesCapitalGrowth || '');
  const [otherAlternativesValue, setOtherAlternativesValue] = useState(savedState.otherAlternativesValue || '');
  const [otherAlternativesCapitalGrowth, setOtherAlternativesCapitalGrowth] = useState(savedState.otherAlternativesCapitalGrowth || '');
  
  // Infrastructure (from SMSF alternatives)
  const [infrastructurePct, setInfrastructurePct] = useState(savedState.infrastructurePct || 0);
  const [infrastructureValue, setInfrastructureValue] = useState(savedState.infrastructureValue || '');
  const [infrastructureReturnRate, setInfrastructureReturnRate] = useState(savedState.infrastructureReturnRate || 6.0);
  
  // Asset details for dynamic UI (shares, infrastructure with names and values)
  const [assetDetails, setAssetDetails] = useState(savedState.assetDetails || null);
  
  // NEW: Multi-asset arrays from SMSF
  const [residentialProperties, setResidentialProperties] = useState(savedState.residentialProperties || []);
  const [commercialProperties, setCommercialProperties] = useState(savedState.commercialProperties || []);
  const [alternativeAssets, setAlternativeAssets] = useState(savedState.alternativeAssets || []);
  const [collectiblesAssets, setCollectiblesAssets] = useState(savedState.collectiblesAssets || []);
  
  // Earning vs Non-Earning Aggregation (from SMSF transfers)
  const [earningAssetsValue, setEarningAssetsValue] = useState(savedState.earningAssetsValue || '');
  const [nonEarningAssetsValue, setNonEarningAssetsValue] = useState(savedState.nonEarningAssetsValue || '');
  const [earningAssetsPct, setEarningAssetsPct] = useState(savedState.earningAssetsPct || '');
  const [nonEarningAssetsPct, setNonEarningAssetsPct] = useState(savedState.nonEarningAssetsPct || '');
  
  // What-if scenario state variables
  const [whatIfRetirementPeriod, setWhatIfRetirementPeriod] = useState(0);
  const [whatIfRetirementPeriodResult, setWhatIfRetirementPeriodResult] = useState(null);
  const [whatIfFeeChangePct, setWhatIfFeeChangePct] = useState(0);
  const [whatIfFeeChangeResult, setWhatIfFeeChangeResult] = useState(null);
  const [whatIfSequenceRisk, setWhatIfSequenceRisk] = useState(-20);
  const [whatIfSequenceRiskResult, setWhatIfSequenceRiskResult] = useState(null);
  
  // Rent Assistance
  const [rentPaidFortnightly, setRentPaidFortnightly] = useState(savedState.rentPaidFortnightly || '');

  const [lumpSumWithdrawals, setLumpSumWithdrawals] = useState(
    normalizeLumpSumWithdrawalRows(savedState.lumpSumWithdrawals)
  );
  const [lumpSumContributions, setLumpSumContributions] = useState(
    normalizeLumpSumContributionRows(savedState.lumpSumContributions)
  );

  // Check for transfer data on mount and when hash changes to 'advanced'
  useEffect(() => {
    const checkForTransferData = () => {
      console.log('DEBUG: AdvancedState checking for transfer data');
      const transferMeta = CalculatorBridge.peekTransferPackage('advanced');
      if (transferMeta) {
        trackTransferReceived({
          source: transferMeta.sourceCalculator,
          destination: transferMeta.targetCalculator
        });
      }
      const transferData = CalculatorBridge.retrieveTransferData('advanced');
      if (transferData) {
        console.log('✅ Auto-populating fields with transfer data');
        console.log('  - sp500:', transferData.sp500);
        console.log('  - ausShares:', transferData.ausShares);
        console.log('  - cash:', transferData.cash);
        console.log('  - infrastructurePct:', transferData.infrastructurePct);
        console.log('  - residentialPropertyValue:', transferData.residentialPropertyValue);
        console.log('  - commercialPropertyValue:', transferData.commercialPropertyValue);
        // Update all relevant state from transfer data
        if (transferData.relationshipStatus) setRelationshipStatus(transferData.relationshipStatus);
        if (transferData.yourAge !== undefined) setYourAge(transferData.yourAge);
        if (transferData.yourSuper !== undefined) setYourSuper(transferData.yourSuper);
        if (transferData.partnerAge !== undefined) setPartnerAge(transferData.partnerAge);
        if (transferData.partnerSuper !== undefined) setPartnerSuper(transferData.partnerSuper);
        if (transferData.adminFees !== undefined) setAdminFees(transferData.adminFees);
        if (transferData.adminFeePercentage !== undefined) setAdminFeePercentage(transferData.adminFeePercentage);
        if (transferData.investmentFees !== undefined) setInvestmentFees(transferData.investmentFees);
        if (transferData.insuranceFees !== undefined) setInsuranceFees(transferData.insuranceFees);
        if (transferData.sp500 !== undefined) setSp500(transferData.sp500);
        if (transferData.ausShares !== undefined) setAusShares(transferData.ausShares);
        if (transferData.intlShares !== undefined) setIntlShares(transferData.intlShares);
        if (transferData.bonds !== undefined) setBonds(transferData.bonds);
        if (transferData.cash !== undefined) setCash(transferData.cash);
        if (transferData.frankingCredits !== undefined) setFrankingCredits(transferData.frankingCredits);
        if (transferData.isHomeowner !== undefined) setIsHomeowner(transferData.isHomeowner);
        if (transferData.includePension !== undefined) setIncludePension(transferData.includePension);
        if (transferData.startYear !== undefined) setStartYear(transferData.startYear);
        if (transferData.periodLength !== undefined) setPeriodLength(transferData.periodLength);
        
        // Phased Retirement data from Accumulation Calculator
        if (transferData.phasedRetirement !== undefined) setPhasedRetirement(transferData.phasedRetirement);
        if (transferData.yourRetirementAge !== undefined) setYourRetirementAge(transferData.yourRetirementAge);
        if (transferData.partnerRetirementAge !== undefined) setPartnerRetirementAge(transferData.partnerRetirementAge);
        if (transferData.yourSalary !== undefined) setYourSalary(transferData.yourSalary);
        if (transferData.partnerSalary !== undefined) setPartnerSalary(transferData.partnerSalary);
        if (transferData.yourSuperContributions !== undefined) setYourSuperContributions(transferData.yourSuperContributions);
        if (transferData.partnerSuperContributions !== undefined) setPartnerSuperContributions(transferData.partnerSuperContributions);
        
        // Alternative Assets data from SMSF Calculator
        if (transferData.transferFromSMSF !== undefined) setTransferFromSMSF(transferData.transferFromSMSF);
        if (transferData.residentialPropertyValue !== undefined) setResidentialPropertyValue(transferData.residentialPropertyValue);
        if (transferData.residentialRentalIncome !== undefined) setResidentialRentalIncome(transferData.residentialRentalIncome);
        if (transferData.residentialPropertyRentalYield !== undefined) setResidentialPropertyRentalYield(transferData.residentialPropertyRentalYield);
        if (transferData.residentialPropertyCapitalGrowth !== undefined) setResidentialPropertyCapitalGrowth(transferData.residentialPropertyCapitalGrowth);
        if (transferData.commercialPropertyValue !== undefined) setCommercialPropertyValue(transferData.commercialPropertyValue);
        if (transferData.commercialRentalIncome !== undefined) setCommercialRentalIncome(transferData.commercialRentalIncome);
        if (transferData.commercialPropertyRentalYield !== undefined) setCommercialPropertyRentalYield(transferData.commercialPropertyRentalYield);
        if (transferData.commercialPropertyCapitalGrowth !== undefined) setCommercialPropertyCapitalGrowth(transferData.commercialPropertyCapitalGrowth);
        if (transferData.collectiblesValue !== undefined) setCollectiblesValue(transferData.collectiblesValue);
        if (transferData.collectiblesCapitalGrowth !== undefined) setCollectiblesCapitalGrowth(transferData.collectiblesCapitalGrowth);
        
        // NEW: Multi-asset arrays from SMSF
        if (transferData.residentialProperties) setResidentialProperties(transferData.residentialProperties);
        if (transferData.commercialProperties) setCommercialProperties(transferData.commercialProperties);
        if (transferData.alternativeAssets) setAlternativeAssets(transferData.alternativeAssets);
        if (transferData.collectiblesAssets) setCollectiblesAssets(transferData.collectiblesAssets);
        if (transferData.otherAlternativesValue !== undefined) setOtherAlternativesValue(transferData.otherAlternativesValue);
        if (transferData.otherAlternativesCapitalGrowth !== undefined) setOtherAlternativesCapitalGrowth(transferData.otherAlternativesCapitalGrowth);
        
        // REMOVED: Do NOT add rental income to otherTaxableIncome
        // Rental income is calculated dynamically in the simulation loop at real growth rate
        // Adding it here would cause double-counting (once static, once growing)
        
        // CRITICAL: Ensure otherTaxableIncome is set to what was transferred (should be 0 from SMSF)
        if (transferData.otherTaxableIncome !== undefined) {
          setOtherTaxableIncome(transferData.otherTaxableIncome);
        }
        if (transferData.otherNonTaxableIncome !== undefined) {
          setOtherNonTaxableIncome(transferData.otherNonTaxableIncome);
        }
        if (transferData.otherAssets !== undefined) {
          setOtherAssets(transferData.otherAssets);
        }
        if (transferData.pensionGrowthRate !== undefined) {
          setPensionGrowthRate(transferData.pensionGrowthRate);
        }
        if (transferData.monteCarloTargetIncome !== undefined) {
          setMonteCarloTargetIncome(transferData.monteCarloTargetIncome);
        }
        if (transferData.withdrawalStrategy !== undefined) {
          setWithdrawalStrategy(transferData.withdrawalStrategy);
        }
        
        // Infrastructure (from SMSF alternatives)
        if (transferData.infrastructurePct !== undefined) setInfrastructurePct(transferData.infrastructurePct);
        if (transferData.infrastructureValue !== undefined) setInfrastructureValue(transferData.infrastructureValue);
        if (transferData.infrastructureReturnRate !== undefined) setInfrastructureReturnRate(transferData.infrastructureReturnRate);
        
        // Asset details for dynamic UI
        if (transferData.assetDetails !== undefined) setAssetDetails(transferData.assetDetails);
      
      // Earning vs Non-Earning Aggregation
      if (transferData.earningAssetsValue !== undefined) setEarningAssetsValue(transferData.earningAssetsValue);
      if (transferData.nonEarningAssetsValue !== undefined) setNonEarningAssetsValue(transferData.nonEarningAssetsValue);
      if (transferData.earningAssetsPct !== undefined) setEarningAssetsPct(transferData.earningAssetsPct);
      if (transferData.nonEarningAssetsPct !== undefined) setNonEarningAssetsPct(transferData.nonEarningAssetsPct);

      // NOTE: Productive asset percentages (sp500, ausShares, intlShares, bonds, cash) are already
      // correctly calculated in SMSFUIIntegration to sum to 100% of productive assets.
      // DO NOT normalize or scale them here - they are already set correctly above (lines 154-158).
        
        // Persist the applied transfer to localStorage so subsequent mounts load correct values
        try {
          const stateToPersist = {
            relationshipStatus: transferData.relationshipStatus,
            yourGender: transferData.yourGender,
            yourAge: transferData.yourAge,
            startYear: transferData.startYear ?? 1980,
            periodLength: transferData.periodLength ?? 35,
            targetFinalBalance: transferData.targetFinalBalance ?? 0,
            yourSuper: transferData.yourSuper,
            yourTbcUsed: transferData.yourTbcUsed,
            partnerGender: transferData.partnerGender,
            partnerSuper: transferData.partnerSuper,
            partnerAge: transferData.partnerAge,
            partnerTbcUsed: transferData.partnerTbcUsed,
            otherTaxableIncome: transferData.otherTaxableIncome,
            isHomeowner: transferData.isHomeowner,
            otherNonTaxableIncome: transferData.otherNonTaxableIncome,
            otherAssets: transferData.otherAssets,
            includePension: transferData.includePension,
            pensionGrowthRate: transferData.pensionGrowthRate,
            generalTransferBalanceCap: transferData.generalTransferBalanceCap,
            hasPrivateHealthInsurance: transferData.hasPrivateHealthInsurance,
            partnerHasPrivateHealthInsurance: transferData.partnerHasPrivateHealthInsurance,
            withdrawalStrategy: transferData.withdrawalStrategy,
            monteCarloTargetIncome: transferData.monteCarloTargetIncome,
            phasedRetirement: transferData.phasedRetirement,
            yourRetirementAge: transferData.yourRetirementAge,
            partnerRetirementAge: transferData.partnerRetirementAge,
            partnerSalary: transferData.partnerSalary,
            partnerSuperContributions: transferData.partnerSuperContributions,
            yourSalary: transferData.yourSalary,
            yourSuperContributions: transferData.yourSuperContributions,
            msiStartsWhenEitherRetires: transferData.msiStartsWhenEitherRetires !== undefined
              ? !!transferData.msiStartsWhenEitherRetires
              : true,
            allowWithdrawalsBeforeRetirementAge: transferData.allowWithdrawalsBeforeRetirementAge ?? false,
            adminFees: transferData.adminFees,
            adminFeePercentage: transferData.adminFeePercentage,
            investmentFees: transferData.investmentFees,
            investmentFeeCap: transferData.investmentFeeCap,
            transactionCosts: transferData.transactionCosts,
            insuranceFees: transferData.insuranceFees,
            sp500: transferData.sp500 ?? 0,
            ausShares: transferData.ausShares ?? 0,
            intlShares: transferData.intlShares ?? 0,
            bonds: transferData.bonds ?? 0,
            cash: transferData.cash ?? 0,
            frankingCredits: transferData.frankingCredits ?? 80,
            annuityPurchasePrice: transferData.annuityPurchasePrice,
            annuityAnnualPayment: transferData.annuityAnnualPayment,
            annuityIsIndexed: transferData.annuityIsIndexed,
            definedBenefitPensionAmount: transferData.definedBenefitPensionAmount,
            definedBenefitIsIndexed: transferData.definedBenefitIsIndexed,
            transferFromSMSF: transferData.transferFromSMSF ?? false,
            residentialPropertyValue: transferData.residentialPropertyValue ?? 0,
            residentialRentalIncome: transferData.residentialRentalIncome ?? 0,
            residentialPropertyRentalYield: transferData.residentialPropertyRentalYield ?? 0,
            residentialPropertyCapitalGrowth: transferData.residentialPropertyCapitalGrowth ?? 0,
            commercialPropertyValue: transferData.commercialPropertyValue ?? 0,
            commercialRentalIncome: transferData.commercialRentalIncome ?? 0,
            commercialPropertyRentalYield: transferData.commercialPropertyRentalYield ?? 0,
            commercialPropertyCapitalGrowth: transferData.commercialPropertyCapitalGrowth ?? 0,
            collectiblesValue: transferData.collectiblesValue ?? 0,
            collectiblesCapitalGrowth: transferData.collectiblesCapitalGrowth ?? 0,
            otherAlternativesValue: transferData.otherAlternativesValue ?? 0,
            otherAlternativesCapitalGrowth: transferData.otherAlternativesCapitalGrowth ?? 0,
            infrastructurePct: transferData.infrastructurePct ?? 0,
            infrastructureValue: transferData.infrastructureValue ?? 0,
            infrastructureReturnRate: transferData.infrastructureReturnRate,
            assetDetails: transferData.assetDetails,
            earningAssetsValue: transferData.earningAssetsValue,
            nonEarningAssetsValue: transferData.nonEarningAssetsValue,
            earningAssetsPct: transferData.earningAssetsPct,
            nonEarningAssetsPct: transferData.nonEarningAssetsPct,
          };
          localStorage.setItem('advanced_calculator_state', JSON.stringify(stateToPersist));
        } catch (_e) {
          // ignore persistence errors
        }
        
        // Clear transfer data after loading
        CalculatorBridge.clearTransferData('advanced');
      } else {
        console.log('DEBUG: No transfer data found for advanced calculator');
      }
    };
    
    // Check immediately on mount
    checkForTransferData();
    
    // Also check when hash changes to 'advanced' (in case component was already mounted)
    const handleHashChange = () => {
      if (window.location.hash === '#advanced') {
        console.log('DEBUG: Hash changed to #advanced, checking for transfer data');
        // Small delay to ensure data was stored
        setTimeout(checkForTransferData, 100);
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const value = useMemo(() => ({
    relationshipStatus,
    setRelationshipStatus,
    yourGender,
    setYourGender,
    yourAge,
    setYourAge,
    startYear,
    setStartYear,
    periodLength,
    setPeriodLength,
    targetFinalBalance,
    setTargetFinalBalance,
    yourSuper,
    setYourSuper,
    yourTbcUsed,
    setYourTbcUsed,
    partnerGender,
    setPartnerGender,
    partnerSuper,
    setPartnerSuper,
    partnerAge,
    setPartnerAge,
    partnerTbcUsed,
    setPartnerTbcUsed,
    otherTaxableIncome,
    setOtherTaxableIncome,
    yourPartTimeEmploymentIncome,
    setYourPartTimeEmploymentIncome,
    partnerPartTimeEmploymentIncome,
    setPartnerPartTimeEmploymentIncome,
    isHomeowner,
    setIsHomeowner,
    otherNonTaxableIncome,
    setOtherNonTaxableIncome,
    otherAssets,
    setOtherAssets,
    includePension,
    setIncludePension,
    pensionGrowthRate,
    setPensionGrowthRate,
    generalTransferBalanceCap,
    setGeneralTransferBalanceCap,
    hasPrivateHealthInsurance,
    setHasPrivateHealthInsurance,
    partnerHasPrivateHealthInsurance,
    setPartnerHasPrivateHealthInsurance,
    withdrawalStrategy,
    setWithdrawalStrategy,
    monteCarloTargetIncome,
    setMonteCarloTargetIncome,
    // Phased Retirement
    phasedRetirement,
    setPhasedRetirement,
    yourRetirementAge,
    setYourRetirementAge,
    partnerRetirementAge,
    setPartnerRetirementAge,
    partnerSalary,
    setPartnerSalary,
    partnerSuperContributions,
    setPartnerSuperContributions,
    yourSalary,
    setYourSalary,
    yourSuperContributions,
    setYourSuperContributions,
    msiStartsWhenEitherRetires,
    setMsiStartsWhenEitherRetires,
    // Super withdrawal age control
    allowWithdrawalsBeforeRetirementAge,
    setAllowWithdrawalsBeforeRetirementAge,
    enableBridgeStrategy,
    setEnableBridgeStrategy,
    yourSuperAccessAge,
    setYourSuperAccessAge,
    partnerSuperAccessAge,
    setPartnerSuperAccessAge,
    bridgeCashBucket,
    setBridgeCashBucket,
    bridgeInvestmentBucket,
    setBridgeInvestmentBucket,
    bridgeInvestmentReturnRate,
    setBridgeInvestmentReturnRate,
    enableMortgageComparison,
    setEnableMortgageComparison,
    mortgageBalance,
    setMortgageBalance,
    mortgageInterestRate,
    setMortgageInterestRate,
    mortgageAnnualRepayment,
    setMortgageAnnualRepayment,
    mortgageStrategy,
    setMortgageStrategy,
    mortgagePayoffSource,
    setMortgagePayoffSource,
    mortgagePayoffScheduleType,
    setMortgagePayoffScheduleType,
    mortgagePayoffScheduleValue,
    setMortgagePayoffScheduleValue,
    mortgagePartialPayoffAmount,
    setMortgagePartialPayoffAmount,
    assumedReturn,
    setAssumedReturn,
    assumedInflation,
    setAssumedInflation,
    adminFees,
    setAdminFees,
    adminFeePercentage,
    setAdminFeePercentage,
    investmentFees,
    setInvestmentFees,
    investmentFeeCap,
    setInvestmentFeeCap,
    transactionCosts,
    setTransactionCosts,
    insuranceFees,
    setInsuranceFees,
    sp500,
    setSp500,
    ausShares,
    setAusShares,
    intlShares,
    setIntlShares,
    bonds,
    setBonds,
    cash,
    setCash,
    frankingCredits,
    setFrankingCredits,
    lastResultIncome,
    setLastResultIncome,
    lastResultSurvival,
    setLastResultSurvival,
    annuityPurchasePrice,
    setAnnuityPurchasePrice,
    annuityAnnualPayment,
    setAnnuityAnnualPayment,
    annuityIsIndexed,
    setAnnuityIsIndexed,
    definedBenefitPensionAmount,
    setDefinedBenefitPensionAmount,
    definedBenefitIsIndexed,
    setDefinedBenefitIsIndexed,
    // Alternative Assets
    transferFromSMSF,
    setTransferFromSMSF,
    residentialPropertyValue,
    setResidentialPropertyValue,
    residentialRentalIncome,
    setResidentialRentalIncome,
    residentialPropertyRentalYield,
    setResidentialPropertyRentalYield,
    residentialPropertyCapitalGrowth,
    setResidentialPropertyCapitalGrowth,
    commercialPropertyValue,
    setCommercialPropertyValue,
    commercialRentalIncome,
    setCommercialRentalIncome,
    commercialPropertyRentalYield,
    setCommercialPropertyRentalYield,
    commercialPropertyCapitalGrowth,
    setCommercialPropertyCapitalGrowth,
    collectiblesValue,
    setCollectiblesValue,
    collectiblesCapitalGrowth,
    setCollectiblesCapitalGrowth,
    otherAlternativesValue,
    setOtherAlternativesValue,
    otherAlternativesCapitalGrowth,
    setOtherAlternativesCapitalGrowth,
    infrastructurePct,
    setInfrastructurePct,
    infrastructureValue,
    setInfrastructureValue,
    infrastructureReturnRate,
    setInfrastructureReturnRate,
    assetDetails,
    setAssetDetails,
    // Multi-asset arrays
    residentialProperties,
    setResidentialProperties,
    commercialProperties,
    setCommercialProperties,
    alternativeAssets,
    setAlternativeAssets,
    collectiblesAssets,
    setCollectiblesAssets,
    whatIfRetirementPeriod,
    setWhatIfRetirementPeriod,
    whatIfRetirementPeriodResult,
    setWhatIfRetirementPeriodResult,
    whatIfFeeChangePct,
    setWhatIfFeeChangePct,
    whatIfFeeChangeResult,
    setWhatIfFeeChangeResult,
    whatIfSequenceRisk,
    setWhatIfSequenceRisk,
    whatIfSequenceRiskResult,
    setWhatIfSequenceRiskResult,
    rentPaidFortnightly,
    setRentPaidFortnightly,
    lumpSumWithdrawals,
    setLumpSumWithdrawals,
    lumpSumContributions,
    setLumpSumContributions,
    allowWithdrawalsBeforeRetirementAge,
    setAllowWithdrawalsBeforeRetirementAge,
  }), [
    relationshipStatus, yourGender, yourAge, startYear, periodLength, targetFinalBalance,
    yourSuper, yourTbcUsed, partnerGender, partnerSuper, partnerAge, partnerTbcUsed,
    otherTaxableIncome, yourPartTimeEmploymentIncome, partnerPartTimeEmploymentIncome, isHomeowner, otherNonTaxableIncome, otherAssets, includePension,
    pensionGrowthRate, generalTransferBalanceCap, hasPrivateHealthInsurance,
    partnerHasPrivateHealthInsurance, withdrawalStrategy, monteCarloTargetIncome,
    phasedRetirement, yourRetirementAge, partnerRetirementAge, partnerSalary, 
    partnerSuperContributions, yourSalary, yourSuperContributions,
    msiStartsWhenEitherRetires,
    assumedReturn, assumedInflation,
    adminFees, adminFeePercentage, investmentFees, investmentFeeCap, transactionCosts,
    insuranceFees, sp500, ausShares, intlShares, bonds, cash, frankingCredits,
    lastResultIncome, lastResultSurvival,
    annuityPurchasePrice, annuityAnnualPayment, annuityIsIndexed, 
    definedBenefitPensionAmount, definedBenefitIsIndexed,
    transferFromSMSF, residentialPropertyValue, residentialRentalIncome, residentialPropertyRentalYield,
    residentialPropertyCapitalGrowth, commercialPropertyValue, commercialRentalIncome, commercialPropertyRentalYield,
    commercialPropertyCapitalGrowth, collectiblesValue, collectiblesCapitalGrowth,
    otherAlternativesValue, otherAlternativesCapitalGrowth,
    infrastructurePct, infrastructureValue, infrastructureReturnRate, assetDetails,
    residentialProperties, commercialProperties, alternativeAssets, collectiblesAssets,
    whatIfRetirementPeriod, whatIfRetirementPeriodResult, whatIfFeeChangePct,
    whatIfFeeChangeResult, whatIfSequenceRisk, whatIfSequenceRiskResult,
    rentPaidFortnightly, allowWithdrawalsBeforeRetirementAge, enableBridgeStrategy, yourSuperAccessAge,
    partnerSuperAccessAge, bridgeCashBucket, bridgeInvestmentBucket, bridgeInvestmentReturnRate,
    enableMortgageComparison, mortgageBalance, mortgageInterestRate, mortgageAnnualRepayment,
    mortgageStrategy, mortgagePayoffSource, mortgagePayoffScheduleType, mortgagePayoffScheduleValue, mortgagePartialPayoffAmount,
    lumpSumWithdrawals, lumpSumContributions
  ]);

  // Autosave state to LocalStorage whenever it changes
  // Skip autosave on initial mount to prevent overwriting transfer data
  const isInitialMountRef = React.useRef(true);
  
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return; // Skip autosave on first render to allow transfer data to load
    }
    
    try {
      const stateToSave = {
        relationshipStatus, yourGender, yourAge, startYear, periodLength, targetFinalBalance,
        yourSuper, yourTbcUsed, partnerGender, partnerSuper, partnerAge, partnerTbcUsed,
        otherTaxableIncome, yourPartTimeEmploymentIncome, partnerPartTimeEmploymentIncome, isHomeowner, otherNonTaxableIncome, otherAssets, includePension,
        pensionGrowthRate, generalTransferBalanceCap, hasPrivateHealthInsurance,
        partnerHasPrivateHealthInsurance, withdrawalStrategy,
        phasedRetirement, yourRetirementAge, partnerRetirementAge, partnerSalary,
        partnerSuperContributions, yourSalary, yourSuperContributions,
        msiStartsWhenEitherRetires,
        assumedReturn, assumedInflation,
        adminFees, adminFeePercentage, investmentFees, investmentFeeCap, transactionCosts, 
        insuranceFees, sp500, ausShares, intlShares, bonds, cash, frankingCredits,
        lastResultIncome, lastResultSurvival,
        annuityPurchasePrice, annuityAnnualPayment, annuityIsIndexed, 
        definedBenefitPensionAmount, definedBenefitIsIndexed,
        transferFromSMSF, residentialPropertyValue, residentialRentalIncome, residentialPropertyRentalYield,
        residentialPropertyCapitalGrowth, commercialPropertyValue, commercialRentalIncome, commercialPropertyRentalYield,
        commercialPropertyCapitalGrowth, collectiblesValue, collectiblesCapitalGrowth,
        otherAlternativesValue, otherAlternativesCapitalGrowth,
        infrastructurePct, infrastructureValue, infrastructureReturnRate, assetDetails,
        rentPaidFortnightly, allowWithdrawalsBeforeRetirementAge, enableBridgeStrategy,
        yourSuperAccessAge, partnerSuperAccessAge, bridgeCashBucket, bridgeInvestmentBucket,
        bridgeInvestmentReturnRate, enableMortgageComparison, mortgageBalance, mortgageInterestRate,
        mortgageAnnualRepayment, mortgageStrategy, mortgagePayoffSource, mortgagePayoffScheduleType,
        mortgagePayoffScheduleValue, mortgagePartialPayoffAmount, lumpSumWithdrawals, lumpSumContributions
      };
      localStorage.setItem('advanced_calculator_state', JSON.stringify(stateToSave));
    } catch (err) {
      console.error('Failed to save state:', err);
    }
  }, [
    relationshipStatus, yourGender, yourAge, startYear, periodLength, targetFinalBalance,
    yourSuper, yourTbcUsed, partnerGender, partnerSuper, partnerAge, partnerTbcUsed,
    otherTaxableIncome, yourPartTimeEmploymentIncome, partnerPartTimeEmploymentIncome, isHomeowner, otherNonTaxableIncome, otherAssets, includePension,
    pensionGrowthRate, generalTransferBalanceCap, hasPrivateHealthInsurance,
    partnerHasPrivateHealthInsurance, withdrawalStrategy, lastResultIncome, lastResultSurvival,
    phasedRetirement, yourRetirementAge, partnerRetirementAge, partnerSalary,
    partnerSuperContributions, yourSalary, yourSuperContributions,
    msiStartsWhenEitherRetires,
    assumedReturn, assumedInflation,
    adminFees, adminFeePercentage, investmentFees, investmentFeeCap, transactionCosts,
    insuranceFees, sp500, ausShares, intlShares, bonds, cash, frankingCredits, 
    annuityPurchasePrice, annuityAnnualPayment, annuityIsIndexed, 
    definedBenefitPensionAmount, definedBenefitIsIndexed,
    transferFromSMSF, residentialPropertyValue, residentialRentalIncome, residentialPropertyRentalYield,
    residentialPropertyCapitalGrowth, commercialPropertyValue, commercialRentalIncome, commercialPropertyRentalYield,
    commercialPropertyCapitalGrowth, collectiblesValue, collectiblesCapitalGrowth,
    otherAlternativesValue, otherAlternativesCapitalGrowth,
    infrastructurePct, infrastructureValue, infrastructureReturnRate, assetDetails,
    rentPaidFortnightly, allowWithdrawalsBeforeRetirementAge, enableBridgeStrategy, yourSuperAccessAge,
    partnerSuperAccessAge, bridgeCashBucket, bridgeInvestmentBucket, bridgeInvestmentReturnRate,
    enableMortgageComparison, mortgageBalance, mortgageInterestRate, mortgageAnnualRepayment,
    mortgageStrategy, mortgagePayoffSource, mortgagePayoffScheduleType, mortgagePayoffScheduleValue, mortgagePartialPayoffAmount,
    lumpSumWithdrawals, lumpSumContributions
  ]);

  return (
    <AdvancedStateContext.Provider value={value}>
      {children}
    </AdvancedStateContext.Provider>
  );
};

export const useAdvancedState = () => {
  const ctx = useContext(AdvancedStateContext);
  if (!ctx) throw new Error('useAdvancedState must be used within AdvancedStateProvider');
  return ctx;
};




